/**
 * FhbfEmbedCard — copy-paste embed snippets for the FHBF Platform +
 * Featured Media blocks. Points at the ecosystem `REACT_APP_BACKEND_URL`
 * so previewing works; the production canonical URL is documented in the
 * card body so operators can override.
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Copy, Code2, ExternalLink } from 'lucide-react';

const backendEnv = process.env.REACT_APP_BACKEND_URL || '';
const PROD_API = 'https://events.faithworldministries.com';
const PROD_SITE = 'https://faithworldministries.org';

const VIEWS = [
  { key: 'home', label: 'Home (hero + stats)', mount: 'fhbf-platform' },
  { key: 'membership', label: 'Membership tiers + Apply', mount: 'fhbf-membership' },
  { key: 'village', label: 'Village stands', mount: 'fhbf-village' },
  { key: 'directory', label: 'Directory', mount: 'fhbf-directory' },
  { key: 'events', label: 'Events', mount: 'fhbf-events' },
  { key: 'sponsors', label: 'Sponsors', mount: 'fhbf-sponsors' },
  { key: 'featured-media', label: 'Featured media (legacy)', mount: 'fhbf-featured-media' },
];

function scriptSnippet({ api, siteOrigin, view, mount, theme }) {
  return `<div id="${mount}"></div>
<script src="${api}/embed/fhbf/platform.js"
        data-target="#${mount}"
        data-api="${api}"
        data-theme="${theme}"
        data-view="${view}"
        data-site-origin="${siteOrigin}"
        defer></script>`;
}

function iframeSnippet({ api, siteOrigin, view, theme }) {
  return `<iframe src="${api}/embed/fhbf/platform.html?view=${view}&theme=${theme}&site-origin=${encodeURIComponent(siteOrigin)}"
        style="border:0;width:100%;min-height:800px"
        title="FHBF ${view}"
        loading="lazy"
        referrerpolicy="strict-origin-when-cross-origin"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"></iframe>`;
}

const CodeBlock = ({ code, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative">
      <pre className="bg-slate-900 border border-slate-800 text-emerald-300 p-3 rounded text-xs overflow-x-auto whitespace-pre"
           data-testid={testid}>{code}</pre>
      <Button size="sm" variant="outline"
              className="absolute top-2 right-2 border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={copy}
              data-testid={`${testid}-copy`}>
        {copied ? 'Copied' : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
      </Button>
    </div>
  );
};

export default function FhbfEmbedCard() {
  const [target, setTarget] = useState('prod');
  const [theme, setTheme] = useState('dark');
  const api = target === 'prod' ? PROD_API : backendEnv;
  const siteOrigin = PROD_SITE;

  const snippets = useMemo(() => {
    return VIEWS.map((v) => ({
      ...v,
      script: scriptSnippet({ api, siteOrigin, view: v.key, mount: v.mount, theme }),
      iframe: iframeSnippet({ api, siteOrigin, view: v.key, theme }),
    }));
  }, [api, siteOrigin, theme]);

  return (
    <Card className="bg-slate-900/60 border-sky-500/30" data-testid="fhbf-embed-card">
      <CardHeader>
        <CardTitle className="text-sm text-white flex items-center gap-2">
          <Code2 className="h-4 w-4 text-sky-300" /> Embed code — paste into one.com / faithworldministries.org
        </CardTitle>
        <CardDescription className="text-slate-400">
          Live platform embed for every view. Membership Apply hits the backend and
          returns a Stripe Checkout URL. Village reservations require login and
          redirect to the member portal.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <span>Target:</span>
            <div className="inline-flex border border-slate-800 rounded overflow-hidden">
              <button
                onClick={() => setTarget('prod')}
                className={`px-3 py-1 ${target === 'prod' ? 'bg-sky-600 text-white' : 'text-slate-300'}`}
                data-testid="embed-target-prod"
              >Production ({PROD_API})</button>
              <button
                onClick={() => setTarget('preview')}
                className={`px-3 py-1 ${target === 'preview' ? 'bg-sky-600 text-white' : 'text-slate-300'}`}
                data-testid="embed-target-preview"
              >Preview ({backendEnv || 'unset'})</button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span>Theme:</span>
            <div className="inline-flex border border-slate-800 rounded overflow-hidden">
              <button onClick={() => setTheme('dark')}
                className={`px-3 py-1 ${theme === 'dark' ? 'bg-sky-600 text-white' : 'text-slate-300'}`}
                data-testid="embed-theme-dark">Dark</button>
              <button onClick={() => setTheme('light')}
                className={`px-3 py-1 ${theme === 'light' ? 'bg-sky-600 text-white' : 'text-slate-300'}`}
                data-testid="embed-theme-light">Light</button>
            </div>
          </div>
        </div>

        <Tabs defaultValue="home">
          <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
            {snippets.map((s) => (
              <TabsTrigger key={s.key} value={s.key} data-testid={`embed-tab-${s.key}`}>{s.label}</TabsTrigger>
            ))}
          </TabsList>
          {snippets.map((s) => (
            <TabsContent key={s.key} value={s.key} className="space-y-3">
              <div>
                <div className="text-xs text-slate-400 mb-1">Drop-in &lt;script&gt; (recommended)</div>
                <CodeBlock code={s.script} testid={`embed-script-${s.key}`} />
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">&lt;iframe&gt; embed (sandboxed)</div>
                <CodeBlock code={s.iframe} testid={`embed-iframe-${s.key}`} />
              </div>
            </TabsContent>
          ))}
        </Tabs>

        <div className="grid md:grid-cols-3 gap-2 text-xs">
          <a href={`${api}/embed/fhbf/platform.js`} target="_blank" rel="noreferrer"
             className="border border-slate-800 rounded p-2 text-slate-300 hover:bg-slate-800 flex items-center justify-between"
             data-testid="fhbf-embed-open-js">
            <span><code>platform.js</code></span><ExternalLink className="h-3 w-3" />
          </a>
          <a href={`${api}/embed/fhbf/platform.html?view=home&theme=${theme}`} target="_blank" rel="noreferrer"
             className="border border-slate-800 rounded p-2 text-slate-300 hover:bg-slate-800 flex items-center justify-between"
             data-testid="fhbf-embed-open-html">
            <span><code>platform.html</code></span><ExternalLink className="h-3 w-3" />
          </a>
          <a href={`${api}/embed/fhbf/README.md`} target="_blank" rel="noreferrer"
             className="border border-slate-800 rounded p-2 text-slate-300 hover:bg-slate-800 flex items-center justify-between"
             data-testid="fhbf-embed-open-readme">
            <span><code>README.md</code></span><ExternalLink className="h-3 w-3" />
          </a>
        </div>

        <div className="border border-emerald-800/40 bg-emerald-900/10 text-emerald-200 rounded p-3 text-xs space-y-1">
          <p><strong>Public platform API:</strong>
            <a className="underline ml-1" href={`${api}/api/public/fhbf/platform?view=home`}
               target="_blank" rel="noreferrer">
              <code>{api}/api/public/fhbf/platform?view=…</code></a></p>
          <p><strong>Directory:</strong>
            <a className="underline ml-1" href={`${api}/api/public/fhbf/directory`}
               target="_blank" rel="noreferrer">
              <code>{api}/api/public/fhbf/directory</code></a></p>
          <p><strong>Featured Media (legacy):</strong>
            <a className="underline ml-1" href={`${api}/api/public/fhbf/featured-media`}
               target="_blank" rel="noreferrer">
              <code>{api}/api/public/fhbf/featured-media</code></a></p>
        </div>
      </CardContent>
    </Card>
  );
}
